import { BadgeCheck, CalendarClock, Clock, KeyRound, MessageSquare, PencilLine, Rocket, Search, Server, ShieldCheck, Smartphone, Timer, UserX, Zap } from 'lucide-react';
import { APP_CONFIG } from '../../config/app';
import type { FaqItem } from '../Faq';
import type { ComparisonRow, OfferBadge, OfferProblemCard, OfferReference, OfferStep, OfferTrustItem } from './types';

export const SETUP_PRICE = 350;
export const MONTHLY_PRICE = 80;
export const COMMITMENT_MONTHS = 12;
export const MAX_PAGES = 10;
export const FIRST_YEAR_TOTAL = SETUP_PRICE + COMMITMENT_MONTHS * MONTHLY_PRICE;
/** Coût mensuel ramené au jour, arrondi à l'euro supérieur. */
export const DAILY_PRICE_CEIL = Math.ceil((MONTHLY_PRICE * 12) / 365);

/** Franchise en base de TVA : à retirer le jour où l'entreprise devient assujettie. */
export const VAT_MENTION = 'TVA non applicable, art. 293 B du CGI';

const numberFormat = new Intl.NumberFormat('fr-FR');

/** Formate un montant avec espaces insécables : "1 310 €". */
export const formatEuro = (amount: number): string =>
  `${numberFormat.format(amount).replace(/[\u202f\u00a0]/g, '\u00a0')}\u00a0€`;

export const WHATSAPP_OFFER_URL = `${APP_CONFIG.contact.whatsappUrl}?text=${encodeURIComponent(
  `Bonjour, je suis intéressé(e) par votre offre de site internet à ${SETUP_PRICE} €. Mon activité : `,
)}`;

export const SEO_TITLE = 'Création site internet auto-entrepreneur & TPE dès 350 € | SOS IT';
export const SEO_DESCRIPTION = `Site internet pro pour auto-entrepreneurs, micro-entrepreneurs et TPE : ${SETUP_PRICE} € puis ${MONTHLY_PRICE} €/mois, maintenance et modifications incluses. Basé près de Genève.`;

export const SERVICE_NAME = 'Création de site internet pour auto-entrepreneurs et TPE';
export const SERVICE_TYPE = 'Création de site internet';
export const SERVICE_AUDIENCE = 'Auto-entrepreneurs, micro-entrepreneurs, artisans, TPE et PME';
export const SERVICE_AREAS: readonly string[] = ['Viry', 'Genève', 'Annemasse', 'Saint-Julien-en-Genevois', 'Pays de Gex', 'Haute-Savoie', 'France'];

export const LOCAL_ZONE_TEXT =
  "Basés à Viry, nous accompagnons les entrepreneurs du Genevois, d'Annemasse, de Saint-Julien-en-Genevois, du Pays de Gex et de toute la Haute-Savoie, sur place ou en visio partout en France.";

export const HERO_BADGES: readonly OfferBadge[] = [
  { label: 'Maintenance & modifications incluses', icon: Server },
  { label: 'Livré en 2 à 3 semaines', icon: Zap },
  { label: 'Interlocuteur unique', icon: BadgeCheck },
];

export const PROBLEM_CARDS: readonly OfferProblemCard[] = [
  {
    title: 'Pas de site internet, pas de clients',
    description: "Artisan ou indépendant, sans site internet, ceux qui vous cherchent sur Google trouvent d'abord un concurrent visible, et c'est lui qu'ils appellent.",
    icon: Search,
  },
  {
    title: 'Un site internet vieillissant fait fuir',
    description: 'Un site internet daté ou mal adapté au mobile donne une mauvaise image, et vos visiteurs repartent avant de vous contacter.',
    icon: Smartphone,
  },
  {
    title: "Pas le temps de s'en occuper",
    description: 'Créer, héberger et mettre à jour un site internet prend du temps que vous préférez consacrer à votre métier.',
    icon: Timer,
  },
];

export const OFFER_STEPS: readonly OfferStep[] = [
  {
    id: '01',
    title: 'Échange',
    description: '15 minutes pour comprendre votre activité, vos clients et ce que votre site internet doit vous apporter.',
    icon: MessageSquare,
  },
  {
    id: '02',
    title: 'Création & mise en ligne',
    description: `On construit, vous validez, puis mise en ligne : ${SETUP_PRICE} € à la mise en ligne.`,
    icon: Rocket,
  },
  {
    id: '03',
    title: 'Suivi mensuel',
    description: `${MONTHLY_PRICE} €/mois : maintenance, mises à jour et modifications de contenu.`,
    icon: Clock,
  },
];

export const SETUP_INCLUDES: readonly string[] = [
  'Design personnalisé à votre image',
  'Responsive mobile et tablette',
  'SEO de base (balises, vitesse, Google Search Console)',
  'Formulaire de contact',
  'Intégration Google Maps et avis',
  'Mentions légales et bandeau cookies',
  'Première version de vos textes rédigée pour vous, à relire et valider',
  'Mise en ligne sur votre hébergement et votre nom de domaine',
  'Livraison en 2 à 3 semaines',
];

export const MONTHLY_INCLUDES: readonly string[] = [
  'Modifications de contenu réalisées sous 7 jours (textes, photos, horaires, tarifs)',
  'Surveillance et sauvegardes du site',
  'Mises à jour de sécurité',
  'Support par email et WhatsApp',
  'Suivi de la fiche Google Business Profile',
];

export const OFFER_FAQ: FaqItem[] = [
  {
    question: 'Un site internet est-il utile pour un micro-entrepreneur ?',
    answer: "Oui. Avant d'appeler, vos futurs clients cherchent sur Google : un site internet vous donne de la crédibilité et vous permet d'être trouvé sur votre secteur et dans votre ville. Il complète votre fiche Google Business Profile en présentant vos services, vos tarifs et vos réalisations, et en facilitant la prise de contact.",
  },
  {
    question: 'Travaillez-vous uniquement autour de Genève ?',
    answer: 'Non. Dans notre zone (Genevois, Annemasse, Saint-Julien-en-Genevois, Pays de Gex et Haute-Savoie), nous pouvons nous déplacer pour un rendez-vous sur place. Pour les autres régions, tout se passe en visio : la création de votre site internet est possible partout en France.',
  },
  {
    question: 'Dois-je écrire les textes moi-même ?',
    answer: "Non : nous rédigeons une première version à partir d'un échange sur votre activité. Vous la relisez, la corrigez si besoin et la validez avant la mise en ligne. Vous fournissez photos et logo si vous en avez.",
  },
  {
    question: 'Et si le résultat ne me plaît pas ?',
    answer: `Vous validez votre site internet avant sa mise en ligne et vos retours sont intégrés ; vous ne payez les ${formatEuro(SETUP_PRICE)} qu'une fois votre site internet en ligne.`,
  },
  {
    question: 'Comment se passe le paiement ?',
    answer: `${formatEuro(SETUP_PRICE)} à la mise en ligne, aucun acompte, puis ${formatEuro(MONTHLY_PRICE)} par mois. ${VAT_MENTION}.`,
  },
  {
    question: 'Que se passe-t-il après 12 mois ?',
    answer: `Après la période d'engagement de ${COMMITMENT_MONTHS} mois, votre abonnement continue sans engagement : vous pouvez le résilier chaque mois, sans frais ni justification.`,
  },
  {
    question: "L'hébergement et le nom de domaine sont-ils inclus ?",
    answer: "Non. Vous les souscrivez directement à votre nom chez l'hébergeur de votre choix, pour quelques euros par mois. Nous vous conseillons une offre adaptée, et nous nous occupons de toute la configuration et de la mise en ligne.",
  },
  {
    question: "Le site internet m'appartient-il ?",
    answer: "Oui. Le nom de domaine et l'hébergement sont à votre nom, et les contenus et textes vous appartiennent. Si vous nous quittez après la période d'engagement, vous gardez tout, et nous vous fournissons une sauvegarde de vos contenus.",
  },
  {
    question: 'Que comprennent les modifications mensuelles ?',
    answer: `Les mises à jour de contenu (textes, photos, horaires, tarifs), réalisées sous 7 jours. Les nouvelles fonctionnalités ou les pages supplémentaires au-delà de ${MAX_PAGES} font l'objet d'un devis.`,
  },
  {
    question: `Et si j'ai besoin de plus de ${MAX_PAGES} pages ou d'un e-commerce ?`,
    answer: "Ces projets sortent du cadre de l'offre et sont réalisés sur devis personnalisé. Contactez-nous pour en discuter.",
  },
  {
    question: 'Combien de temps pour être en ligne ?',
    answer: 'Comptez 2 à 3 semaines après réception de vos contenus (textes, photos, logo).',
  },
  {
    question: "Pourquoi un abonnement plutôt qu'un prix unique ?",
    answer: `Cela nous permet de proposer la création à ${formatEuro(SETUP_PRICE)} au lieu de plusieurs milliers d'euros : le coût est étalé sur la première année (engagement de ${COMMITMENT_MONTHS} mois), puis vous êtes libre chaque mois. En plus, votre site internet reste à jour et sécurisé, avec un interlocuteur dédié.`,
  },
];

/**
 * Références clients. Pour ajouter un témoignage réel ou une capture d'écran,
 * renseigner les champs optionnels `testimonial` ({ quote, author, role }) et
 * `screenshot` (chemin dans /public/images) sur la référence concernée :
 * ils ne sont rendus que s'ils existent. Ne rien inventer.
 */
export const REFERENCES: readonly OfferReference[] = [
  {
    name: 'Val Drôme Guêpes Frelons',
    category: 'Artisan · Destruction de nids',
    href: 'https://frelons-guepes-destruction.fr/',
    screenshot: '/images/references/valdrome-site.webp',
    description: "Création du site vitrine d'un spécialiste de la destruction de nids de guêpes et frelons dans la vallée de la Drôme.",
  },
  {
    name: 'Auberge Fruitière de Péron',
    category: 'Restaurant & hébergement',
    href: 'https://aubergefruitiereperon.fr/',
    screenshot: '/images/references/auberge-site.webp',
    description: "Création du site vitrine et mise en valeur de l'établissement.",
  },
  {
    name: 'Avis Clients AI',
    category: 'Startup SaaS',
    href: 'https://avis-clients.ai/',
    screenshot: '/images/references/avis-clients-site.webp',
    description: "Développement de la plateforme SaaS et de l'interface utilisateur.",
  },
];

export const COMPARISON_COLUMNS = {
  agency: 'Agence web',
  diy: 'Faire soi-même (Wix, etc.)',
  sos: 'SOS IT Services',
} as const;

export const COMPARISON_ROWS: readonly ComparisonRow[] = [
  {
    criterion: 'Prix de départ',
    agency: { text: "Plusieurs milliers d'euros", level: 'bad' },
    diy: { text: 'Abonnement + votre temps', level: 'neutral' },
    sos: { text: formatEuro(SETUP_PRICE), level: 'good' },
  },
  {
    criterion: 'Maintenance et mises à jour',
    agency: { text: 'En supplément', level: 'bad' },
    diy: { text: 'À gérer vous-même', level: 'bad' },
    sos: { text: 'Inclus', level: 'good' },
  },
  {
    criterion: 'Modifications',
    agency: { text: 'Facturées', level: 'bad' },
    diy: { text: 'À faire vous-même', level: 'neutral' },
    sos: { text: 'Incluses, sous 7 jours', level: 'good' },
  },
  {
    criterion: 'Référencement Google',
    agency: { text: 'Variable', level: 'neutral' },
    diy: { text: 'Basique', level: 'neutral' },
    sos: { text: 'Optimisé dès la création', level: 'good' },
  },
  {
    criterion: 'Interlocuteur',
    agency: { text: 'Équipe changeante', level: 'neutral' },
    diy: { text: 'Aucun', level: 'bad' },
    sos: { text: 'Un interlocuteur unique', level: 'good' },
  },
  {
    criterion: 'Temps à y consacrer',
    agency: { text: 'Réunions', level: 'neutral' },
    diy: { text: 'Beaucoup', level: 'bad' },
    sos: { text: 'Relire et valider', level: 'good' },
  },
];

export const TRUST_ITEMS: readonly OfferTrustItem[] = [
  {
    problem: 'Mon prestataire a disparu, plus personne ne répond.',
    commitment: 'Un interlocuteur unique, joignable par WhatsApp et email.',
    icon: UserX,
  },
  {
    problem: 'Mon site est bloqué chez l\'ancien prestataire, je ne peux pas partir.',
    commitment: 'Nom de domaine et hébergement à votre nom dès le premier jour : votre site vous appartient.',
    icon: KeyRound,
  },
  {
    problem: 'Chaque petite modification est facturée.',
    commitment: 'Modifications de contenu incluses, réalisées sous 7 jours.',
    icon: PencilLine,
  },
  {
    problem: 'Ça devait prendre un mois, ça en a pris six.',
    commitment: 'Mise en ligne en 2 à 3 semaines après réception de vos contenus, et aucun acompte : vous payez à la mise en ligne.',
    icon: CalendarClock,
  },
  {
    problem: 'Mon site n\'a jamais été mis à jour, il a fini par être piraté.',
    commitment: 'Mises à jour de sécurité, surveillance et sauvegardes incluses.',
    icon: ShieldCheck,
  },
];
